<script setup>
import { ref, inject, computed } from 'vue';
import { ChevronLeft } from '@lucide/vue';
import { getPrettyPrice, getCardPrice, convertTsToLocal } from '../shared/utils';
import { useCustomerStore } from '../store/customer';
import useCartStore from '../store/cart';
import useOrderStore from '../store/order';
import { useScheduleStore } from '../store/schedule';
import router from '../routes';
import DateTimePicker from './DateTimePicker.vue';
import PaymentForm from './PaymentForm.vue';
import LineItems from './LineItems.vue';
import OrderTotal from './OrderTotal.vue';
import ModalHead from './ModalHead.vue';
import ModalFooter from './ModalFooter.vue';
import ModalBtn from './ModalBtn.vue';

import FieldGroup from '@client/js/components/FieldGroup.vue';
import Accordion from '@client/js/components/Accordion.vue';
import CustomerFields from '@client/js/components/CustomerFields.vue';
import DeliveryFields from '@client/js/components/DeliveryFields.vue';
import CustomerCard from '@client/js/components/CustomerCard.vue';
import ExpandableTextarea from '@client/js/components/ExpandableTextarea.vue';

import { useCloverSDK } from '@client/js/composables/useCloverSDK.js';
const { isSdkReady } = useCloverSDK();

const rootData = inject('rootData');
const restApi = inject('restApi');

const paymentForm = ref(null);
const checkoutBtn = ref(null);
const inProgress = ref(false);
const checkoutResponseMessage = ref('');

const customerStore = useCustomerStore();
const orderStore = useOrderStore();
const scheduleStore = useScheduleStore();
const cartStore = useCartStore();

// Single source of truth for "are there blocking checkout errors". Used by both
// the error panel (v-show) and the Place Order button (:disabled) so the two can
// never drift. Uber Direct delivers ASAP, so its hidden date/time picker must
// not count as an invalid schedule.
const hasCheckoutErrors = computed(() =>
  !customerStore.meta.valid
  || customerStore.deliveryBlocked
  || (!customerStore.isUberDelivery && !scheduleStore?.isValid)
  || paymentForm.value?.hasErrors
);

// const selectedDate = ref('');
// const selectedTime = ref('');

// const getSheduledDateEvent = function (dtPair) {
//     if (Array.isArray(dtPair) && dtPair.length == 2) {
//         let [day, time] = dtPair;
//         selectedDate.value = day;
//         selectedTime.value = time;
//     }
//     console.log("Emitted", selectedDate.value, selectedTime.value);
// }

// https://developers.google.com/maps/documentation/address-validation/usage-and-billing
// TODO: Google geocoding and address validation

const processCheckoutResponse = ( response ) => {
    console.log('start processing ', response);
    if ( response?.status ) {
        let responsePayload = response?.data?.data;
        if ( responsePayload?.customer_id ) {
            console.log('customer exists');
            customerStore.customerId = responsePayload?.customer_id;
        }
        if ( response?.status >= 200 && response?.status < 300 ) {
            console.log('response positive');
            if (responsePayload?.order_id) {
                orderStore.orderId = responsePayload?.order_id;
                orderStore.paymentStatus = response?.data?.meta?.payment_status;
                orderStore.paymentMessage = response?.data?.meta?.payment_msg;
                router.push('/thank-you/');
            } else {
                console.log('response negative');  
            }
        } else if ( response?.status >= 400 ) {
            if (response?.response?.data?.message) {
                let msg = response?.response?.data?.message.replaceAll('_', ' ');
                checkoutResponseMessage.value = msg.charAt(0).toUpperCase() + msg.slice(1)
            }
            console.log('response negative 400', response);  
        }
    } else {
        console.log('unknown');  
    }

}
const handleCheckout = async () => {
    checkoutResponseMessage.value = '';
    // let dayStored = localStorage.getItem(dateStorageKey);
    // let timeStored = localStorage.getItem(timeStorageKey);
    // console.log("DAY: %s, TIME: %s", dayStored, timeStored);

    // let selectedDate = scheduleStore.selectedDate;
    // let selectedTime = scheduleStore.selectedTime;
    // console.log('Full Checkout, Date/Time: ', selectedDate, selectedTime);
    // if (Number.isInteger(scheduleStore.selectedDate) && Number.isInteger(scheduleStore.selectedTime)) {
    //     let timestamp = new Date(scheduleStore.selectedDate);
    //     timestamp.setMinutes(Number(scheduleStore.selectedTime));
    //     ({ date: selectedDate, time: selectedTime } = convertTsToLocal(timestamp));
    // }

    console.log('🧾 Handle Full Checkout: Date/Time - %s %s', scheduleStore.formattedSelectedDate, scheduleStore.formattedSelectedTime);
    // console.log('Full Checkout, Date/Time: ', selectedDate, selectedTime, scheduleStore.formattedSelectedDate, scheduleStore.formattedSelectedTime);

    let address = String(customerStore.isDeliveryOrder ? `${customerStore?.street}, ${customerStore?.city}, ${customerStore?.state}, ${customerStore?.zip}` : '');
    console.debug("🧾 Handle Full Checkout: payment option is ", customerStore.paymentOption);
    const order = {
        "orderCart": {
            "lineItems": Object.values(cartStore.contents),
            "note": String(customerStore?.instructions ? customerStore.instructions : ''),
            "orderType": {
                "id":   rootData.services[customerStore.orderType].cid,
                "label": rootData.services[customerStore.orderType].name
            },
            "merchant": {
                "id": rootData.merchantUuid
            },
            "paymentMethod": customerStore.paymentOption,
        },
        "orderTypeId": rootData.services[customerStore.orderType].id,
        "orderType": customerStore.orderType,
        "customerId": customerStore.customerId,
        // Uber Direct delivers ASAP with no scheduled window, so its date/time
        // picker is hidden and the schedule store stays null. Send the literal
        // 'today'/'asap' the checkout endpoint accepts, instead of null (which
        // fails the day/time validators → "Invalid parameter(s): order").
        "orderTypeParams": {
            "day": customerStore.isUberDelivery ? "today" : scheduleStore.formattedSelectedDate,
            "time": customerStore.isUberDelivery ? "asap" : scheduleStore.formattedSelectedTime,
            "date": scheduleStore.isoSelectedDate,    // 'YYYY-MM-DD' in merchant TZ, null for ASAP
            "timeIso": scheduleStore.isoSelectedTime, // 'HH:MM' (24h), null for ASAP
            "address": address,
            "uberQuoteId": customerStore.uberQuote?.quoteId || null,
        },
    }
    const customer = {
        "phoneNumber": customerStore.phoneNumber,
        "email": customerStore.email,
        "firstName": customerStore.firstName,
        "lastName": customerStore.lastName,
        "street": customerStore.street,
        "city": customerStore.city,
        "state": customerStore.state,
        "zip": customerStore.zip,
        "lat": customerStore.lat,
        "lng": customerStore.lng,
        "id": customerStore.customerId,
    }
    const payment = {
        amount: Number(getCardPrice(cartStore.checkoutTotal)).toFixed(0),
        token: 'clv_token',
    }

    try {
        inProgress.value = true;
        let tokenizerResult = null;
        try {
            console.log('🧾 Checkout: Before Tokenizer');
            tokenizerResult = await paymentForm.value.clover.createToken();
            console.log('🧾 Checkout: Tokenizer', tokenizerResult);
        } catch (error) {
            console.error('🍀 Card createToken error: %o', error);
        }
        console.log('🧾 Checkout: After Tokenizer', tokenizerResult);
        if (! tokenizerResult?.token) {
            checkoutResponseMessage.value = 'There was an issue with processing your card. Please try again.';
            console.error('🍀 Card tokenization error: %o', tokenizerResult);
            throw new Error('Card tokenization error');
        }
        console.log('🧾 Checkout: Card Token', tokenizerResult.token);
        payment.token = tokenizerResult.token;
        let checkoutResponse = await restApi
            .post('clover/checkout', {
                order,
                customer,
                payment,
                message: '' // honeypot ^^
            });
        console.log('🧾 Checkout: Response is ', checkoutResponse);
        processCheckoutResponse(checkoutResponse);
    } catch (data) {
        console.warn('Catch: ', data);
        let debugErrorMsg = 'Order processing error: ';
        let userErrorMsg = '';
        if (data?.errors) {
            console.warn('Catch: data.errors: ', data.errors);
            debugErrorMsg += Object.values(data.errors).join(', ');
            userErrorMsg = Object.values(data.errors)[0];
        } else if (data?.message) {
            console.warn('Catch: data.message: ', data.message);
            debugErrorMsg += data.message;
            userErrorMsg = data.message;
        }
        checkoutResponseMessage.value = userErrorMsg;
        processCheckoutResponse(data);
    } finally {
        inProgress.value = false;
    }
}
</script>

<template>
    <ModalHead>
        <template #additional>
        <router-link to="/cart/">
            <ChevronLeft/>
        </router-link>
        </template>
    </ModalHead>
    <div class="modal__body">

        <!-- Uber Direct delivers ASAP only; scheduling doesn't apply, so hide
             the date/time picker for that provider. -->
        <keep-alive v-if="!customerStore.isUberDelivery">
            <DateTimePicker/>
        </keep-alive>
        <p v-else class="uber-asap-note">
            Delivery as soon as possible — your courier is dispatched right after checkout.
        </p>

        <div class="fieldsets">

            <FieldGroup class="bordered" v-if="customerStore.isCompleteCustomerData">
                <template #legend>Customer</template>
                <CustomerCard/>
            </FieldGroup>

            <FieldGroup v-else>
                <template #legend>Customer</template>
                <CustomerFields/>
            </FieldGroup>

            <FieldGroup v-if="customerStore.isDeliveryOrder">
                <template #legend>Deliver order to</template>
                <DeliveryFields/>
            </FieldGroup>

            <FieldGroup v-if="customerStore.paymentOption == 'card'" class="bordered">
                <template #legend>Payment</template>
                <keep-alive>
                    <PaymentForm v-if="isSdkReady" ref="paymentForm"/>
                </keep-alive>
            </FieldGroup>

            <FieldGroup class="no-border">
                <ExpandableTextarea
                    label="Add a note to your order:"
                    placeholder="e.g. No onions, please."
                    v-model="customerStore.instructions"
                    :inputProps="customerStore.instructionsProps"
                    button-open-label="Add Special Instructions"
                    button-close-label="Hide Instructions"
                />
            </FieldGroup>
        </div>

        <Accordion>
            <template #heading>Order Details</template>
            <line-items :controls="false" :hideCash="true" />
        </Accordion>

        <OrderTotal :hideCash="true" />

        <div class="checkoutErrors" v-show="hasCheckoutErrors">
            <div class="checkoutErrorsTitle">Check these things:</div>
            <div class="checkoutError" v-if="scheduleStore.errors?.selectedDate">{{ scheduleStore.errors.selectedDate }}</div>
            <div class="checkoutError" v-if="scheduleStore.errors?.selectedTime">{{ scheduleStore.errors.selectedTime }}</div>
            <div class="checkoutError" v-if="!customerStore.isUberDelivery && !scheduleStore.selectedDate && !scheduleStore.selectedTime">Date/Time data is required</div>
            <div class="checkoutError" v-for="err in customerStore.errors">{{ err }}</div>
            <div class="checkoutError" v-if="customerStore.deliveryBlocked">Delivery is not available to the selected address</div>
            <div class="checkoutError" v-if="paymentForm?.firstError">{{ paymentForm?.firstError }}</div>
            <div class="checkoutError" v-else-if="paymentForm?.hasErrors">Valid payment data is required</div>
        </div>

        <div class="requestErrors" v-show="checkoutResponseMessage.length > 0">
            <div class="requestError" v-text="checkoutResponseMessage"></div>
        </div>

    </div>
    <ModalFooter>
        <ModalBtn
            @click="handleCheckout"
            form="checkout-form"
            class="btn-primary btn-action"
            :data-processing="inProgress ||null"
            :disabled="hasCheckoutErrors">
                Place Order • {{ getPrettyPrice(getCardPrice(cartStore.checkoutTotal)) }}
        </ModalBtn>
    </ModalFooter>
</template>

<style lang="scss" scoped>
.cart-total {
    margin-left: -1rem;
    margin-right: -1rem;
}

.checkoutErrors,
.requestErrors {
    background-color: #fff0f5;
    border: 2px solid #ffafbb;
    padding: 0.75rem 1.5rem 0.75rem 0.75rem;
    border-radius: 0.375rem;
    font-size: 0.75rem;
    margin-bottom: 1.25rem;
    color: var(--text-secondary);

    &Title {
        font-weight: 700;
        letter-spacing: 0.25px;
        margin-bottom: 0.25rem;
    }
}

.requestError,
.checkoutError {
    margin-bottom: 0.125rem;
}

dl {
    border-top: none;
}

.fieldsets  fieldset:first-child {
    margin-bottom: 1rem;
}

.modal {
    &__body {
        padding: 1rem;
        min-height: 80vh;
        min-height: calc(100vh - 170px);
        min-height: calc(100svh - 170px);
        padding-bottom: 2rem;

        @media (min-width: 576px) {
            min-height: auto;
        }
    }
}
</style>